import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import Badge from '../components/Badge';
import StateBlock from '../components/StateBlock';
import DocumentWallet from '../components/DocumentWallet';
import AuditLog from '../components/AuditLog';
import EntityLink from '../components/EntityLink';
import { useToast } from '../context/FeedbackContext';

const INVOICE_TYPE_LABELS = { standard: 'Standard', passthrough: 'Allowance / Reimbursement' };

function fmt(n) {
  return '$' + (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function Field({ label, value, link }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--muted)' }}>{label}</div>
      <div>{link && value ? <EntityLink type={link.type} id={link.id}>{value}</EntityLink> : (value || '—')}</div>
    </div>
  );
}

export default function APInvoiceProfilePage() {
  const toast = useToast();
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  function load() {
    setError(false);
    api.get(`/ap-invoices/${id}`)
      .then(r => setData(r.data))
      .catch(() => setError(true));
  }
  useEffect(() => { load(); }, [id]);

  if (error) {
    return <StateBlock type="error" action={<button className="btn btn-primary" onClick={load}>Try again</button>} />;
  }
  if (!data) return <StateBlock type="loading" />;

  const handlePaymentAdvice = async () => {
    try {
      const response = await api.get(`/payment-advice/single/${data.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      // Read the real filename (PA-<reference>.pdf) from the server's Content-Disposition
      // header instead of hardcoding one — keeps the downloaded file's name in sync with
      // the reference number actually printed on the PDF. Falls back to a safe default
      // if the header is ever missing.
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      link.setAttribute('download', match ? match[1] : `payment-advice-${data.invoice_number.replace(/\//g,'-')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // responseType 'blob' hides the server's JSON error - read it back out
      let msg = err.message || 'Unknown error';
      try { msg = JSON.parse(await err.response.data.text()).error || msg; } catch {}
      toast('Failed to generate Payment Advice: ' + msg, 'error');
    }
  };
  const handleBGVInvoice = async (paymentId) => {
    try {
      const response = await api.get(`/bgv-invoice/${paymentId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      link.setAttribute('download', match ? match[1] : `bgv-invoice-${paymentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      load(); // refresh so the new document appears in data.documents and the button switches to "Generated"
    } catch (err) {
      toast('Failed to generate BGV Invoice: ' + (err.response?.data?.error || err.message || 'Unknown error'), 'error');
    }
  };

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>← Back</button>
          {data.payments && data.payments.length > 0 && (
            <button className="btn btn-primary" onClick={handlePaymentAdvice} style={{ background: 'var(--accent)', border: 'none' }}>
              📄 Payment Advice
            </button>
          )}
          <h1 className="page-title">{data.invoice_number}</h1>
          <Badge>{data.status}</Badge>
          <Badge variant={data.invoice_type === 'passthrough' ? 'info' : undefined}>
            {INVOICE_TYPE_LABELS[data.invoice_type] || INVOICE_TYPE_LABELS.standard}
          </Badge>
        </div>
      </div>

      <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}>Invoice Details</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          <Field label="Vendor" value={data.vendor_name} link={data.vendor_id ? { type: 'vendor', id: data.vendor_id } : null} />
          <Field label="Contractor" value={data.contractor_name} link={data.contractor_id ? { type: 'contractor', id: data.contractor_id } : null} />
          <Field label="Work Order #" value={data.client_wo_number} link={data.wo_id ? { type: 'work_order', id: data.wo_id } : null} />
          <Field label="PO #" value={data.po_number} link={data.po_id ? { type: 'purchase_order', id: data.po_id } : null} />
          <Field label="Invoice Date" value={data.invoice_date} />
          <Field label="Period" value={data.period_start && data.period_end ? `${data.period_start} – ${data.period_end}` : null} />
          <Field label="Hours Billed" value={data.hours_billed} />
          <Field label="Hours Approved" value={data.hours_approved} />
          <Field label="Rate" value={data.rate ? `$${data.rate}/hr` : null} />
        </div>
        {data.notes && (
          <div style={{ marginTop: 16 }}>
            <Field label="Notes" value={data.notes} />
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 20 }}>
          <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>Amount to Pay</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{fmt(data.amount_to_pay)}</div>
          </div>
          <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>Outstanding</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{fmt(data.summary.outstanding)}</div>
          </div>
        </div>
        {data.advance && (
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 12 }}>
            Contractor advance balance: <strong>{fmt(data.advance.balance)}</strong> (advanced {fmt(data.advance.total_advanced)}, recovered {fmt(data.advance.total_recovered)}) – see the contractor's Advances tab.
          </div>
        )}
      </div>

      <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}>Payments ({(data.payments || []).length})</h3>
        {(!data.payments || data.payments.length === 0) ? (
          <StateBlock type="empty" title="No payments recorded yet" />
        ) : (
          <table className="data-table">
            <thead><tr><th>Date</th><th style={{ textAlign: 'right' }}>Amount</th><th>Reference</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {data.payments.map(p => (
                <tr key={p.id}>
                  <td>{p.payment_date}</td>
                  <td style={{ textAlign: 'right' }}>{fmt(p.cash_amount)}</td>
                  <td>{p.trace_id || '—'}</td>
                  <td><Badge variant={p.verification_status === 'verified' ? 'success' : 'warning'}>{p.verification_status}</Badge></td>
                  <td>
                    {p.bgc_amount > 0 && (() => {
                      const existingDoc = (data.documents || []).find(d => (d.notes || '').includes(`BGV_PAYMENT_ID:${p.id}`));
                      return existingDoc ? (
                        <button className="btn btn-secondary btn-sm" disabled title={`Already generated: ${existingDoc.title} (${new Date(existingDoc.created_at).toLocaleDateString()}) — see Documents tab below`}>
                          ✓ BGV Invoice Generated
                        </button>
                      ) : (
                        <button className="btn btn-secondary btn-sm" onClick={() => handleBGVInvoice(p.id)} title={`Generate a BGV invoice for the ${fmt(p.bgc_amount)} deduction on this payment`}>
                          BGV Invoice
                        </button>
                      );
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="form-hint" style={{ marginTop: 12 }}>
          To edit this invoice or record a payment, use the AP Invoices list.
        </div>
      </div>

      <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
        <DocumentWallet entityType="ap_invoice" entityId={data.id} entityName={data.invoice_number} />
      
      <AuditLog tableName="ap_invoices" recordId={id} /></div>
    </div>
  );
}
